import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Layers, Wifi, WifiOff, Droplets, Gauge, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';

// Fix for default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Device {
  id: string;
  name: string;
  type: 'gateway' | 'valve_node' | 'motor_node' | 'sensor_node';
  status: 'online' | 'offline' | 'warning';
  position: [number, number];
  data?: {
    moisture?: number;
    pressure?: number;
    flow?: number;
    lastUpdate?: string;
  };
}

interface Zone {
  id: string;
  name: string;
  type: 'field' | 'zone';
  geometry: GeoJSON.Feature;
  properties: {
    moisture_level?: number;
    target_moisture?: number;
    status: 'optimal' | 'low' | 'high' | 'alert';
  };
}

const Map: React.FC = () => {
  const { t } = useTranslation('map');
  const [selectedLayers, setSelectedLayers] = useState({
    zones: true,
    devices: true,
    sensors: true,
  });

  // Occitanie region center (Toulouse area)
  const mapCenter: [number, number] = [43.6045, 1.4440];

  // Sample GeoJSON data for Occitanie farms
  const zones: Zone[] = [
    {
      id: 'farm-1',
      name: 'Domaine Les Vignes',
      type: 'field',
      geometry: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [1.4200, 43.6200],
            [1.4400, 43.6200],
            [1.4400, 43.6100],
            [1.4200, 43.6100],
            [1.4200, 43.6200]
          ]]
        }
      },
      properties: {
        moisture_level: 68,
        target_moisture: 65,
        status: 'optimal'
      }
    },
    {
      id: 'farm-2',
      name: 'Coopérative Hérault',
      type: 'field',
      geometry: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [3.8500, 43.6000],
            [3.8800, 43.6000],
            [3.8800, 43.5800],
            [3.8500, 43.5800],
            [3.8500, 43.6000]
          ]]
        }
      },
      properties: {
        moisture_level: 45,
        target_moisture: 60,
        status: 'low'
      }
    },
    {
      id: 'farm-3',
      name: 'Exploitation Gard Sud',
      type: 'field',
      geometry: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [4.3400, 43.8200],
            [4.3700, 43.8200],
            [4.3700, 43.8000],
            [4.3400, 43.8000],
            [4.3400, 43.8200]
          ]]
        }
      },
      properties: {
        moisture_level: 78,
        target_moisture: 65,
        status: 'high'
      }
    }
  ];

  // Sample device data
  const devices: Device[] = [
    {
      id: 'gw-001',
      name: 'Gateway Vignes',
      type: 'gateway',
      status: 'online',
      position: [43.6150, 1.4300],
      data: { lastUpdate: '2 min ago' }
    },
    {
      id: 'vn-001',
      name: 'Valve Zone A-1',
      type: 'valve_node',
      status: 'online',
      position: [43.6180, 1.4320],
      data: { pressure: 3.2, lastUpdate: '1 min ago' }
    },
    {
      id: 'sn-001',
      name: 'Moisture Sensor A-1',
      type: 'sensor_node',
      status: 'online',
      position: [43.6160, 1.4340],
      data: { moisture: 68, lastUpdate: '30 sec ago' }
    },
    {
      id: 'sn-002',
      name: 'Moisture Sensor B-1',
      type: 'sensor_node',
      status: 'warning',
      position: [3.8650, 43.5900],
      data: { moisture: 45, lastUpdate: '5 min ago' }
    },
    {
      id: 'mn-001',
      name: 'Motor Pump Station',
      type: 'motor_node',
      status: 'offline',
      position: [4.3550, 43.8100],
      data: { lastUpdate: '15 min ago' }
    }
  ];

  const getZoneColor = (status: string) => {
    switch (status) {
      case 'optimal': return '#4CAF50';
      case 'low': return '#FF9800';
      case 'high': return '#2196F3';
      case 'alert': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getDeviceIcon = (device: Device) => {
    const color = device.status === 'online' ? '#4CAF50' :
                  device.status === 'warning' ? '#FF9800' : '#F44336';

    let iconHtml = '';
    switch (device.type) {
      case 'gateway':
        iconHtml = `<div style="background: ${color}; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 12px;">📡</div>`;
        break;
      case 'valve_node':
        iconHtml = `<div style="background: ${color}; width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 10px;">💧</div>`;
        break;
      case 'motor_node':
        iconHtml = `<div style="background: ${color}; width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 10px;">⚡</div>`;
        break;
      case 'sensor_node':
        iconHtml = `<div style="background: ${color}; width: 16px; height: 16px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 8px;">📊</div>`;
        break;
    }

    return L.divIcon({
      html: iconHtml,
      className: 'custom-marker',
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
  };

  const zoneStyle = (feature: any) => {
    const zone = zones.find(z => z.id === feature.properties.id);
    return {
      color: getZoneColor(zone?.properties.status || 'optimal'),
      weight: 2,
      opacity: 0.8,
      fillColor: getZoneColor(zone?.properties.status || 'optimal'),
      fillOpacity: 0.2
    };
  };

  const toggleLayer = (layer: keyof typeof selectedLayers) => {
    setSelectedLayers(prev => ({
      ...prev,
      [layer]: !prev[layer]
    }));
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Map Controls */}
      <div className="bg-[var(--surface)] border-b border-[var(--border-light)] p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-heading-3 text-[var(--text-primary)]">{t('title')}</h1>
            <p className="text-body-sm text-[var(--text-secondary)]">
              {t('subtitle')}
            </p>
          </div>

          {/* Layer Controls */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Layers className="w-5 h-5 text-[var(--text-secondary)]" />
              <span className="text-sm font-medium text-[var(--text-primary)]">{t('layers')}</span>
            </div>

            <button
              onClick={() => toggleLayer('zones')}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                selectedLayers.zones
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--gray-200)] text-[var(--text-secondary)]'
              }`}
              data-testid="zones-layer-toggle"
            >
              {t('toggles.zones')}
            </button>

            <button
              onClick={() => toggleLayer('devices')}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                selectedLayers.devices
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--gray-200)] text-[var(--text-secondary)]'
              }`}
              data-testid="devices-layer-toggle"
            >
              {t('toggles.devices')}
            </button>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative">
        <MapContainer
          center={mapCenter}
          zoom={8}
          style={{ height: '100%', width: '100%' }}
          className="z-0"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {/* Zone/Field Polygons */}
          {selectedLayers.zones && zones.map(zone => (
            <GeoJSON
              key={zone.id}
              data={zone.geometry}
              style={zoneStyle}
            >
              <Popup>
                <div className="p-2 min-w-[200px]">
                  <h3 className="font-semibold text-[var(--text-primary)] mb-2">
                    {zone.name}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{t('popup.currentMoisture')}</span>
                      <span className="font-medium">{zone.properties.moisture_level}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{t('popup.target')}</span>
                      <span className="font-medium">{zone.properties.target_moisture}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{t('popup.status')}</span>
                      <span className={`font-medium capitalize px-2 py-1 rounded-full text-xs ${
                        zone.properties.status === 'optimal' ? 'bg-green-100 text-green-800' :
                        zone.properties.status === 'low' ? 'bg-orange-100 text-orange-800' :
                        zone.properties.status === 'high' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {zone.properties.status}
                      </span>
                    </div>
                  </div>
                </div>
              </Popup>
            </GeoJSON>
          ))}

          {/* Device Markers */}
          {selectedLayers.devices && devices.map(device => (
            <Marker
              key={device.id}
              position={device.position}
              icon={getDeviceIcon(device)}
            >
              <Popup>
                <div className="p-2 min-w-[180px]">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-[var(--text-primary)]">
                      {device.name}
                    </h3>
                    <div className="flex items-center space-x-1">
                      {device.status === 'online' ? (
                        <Wifi className="w-4 h-4 text-green-500" />
                      ) : (
                        <WifiOff className="w-4 h-4 text-red-500" />
                      )}
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        device.status === 'online' ? 'bg-green-100 text-green-800' :
                        device.status === 'warning' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {device.status}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{t('popup.type')}</span>
                      <span className="font-medium capitalize">{device.type.replace('_', ' ')}</span>
                    </div>

                    {device.data?.moisture && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">{t('popup.humidity')}</span>
                        <span className="font-medium">{device.data.moisture}%</span>
                      </div>
                    )}

                    {device.data?.pressure && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">{t('popup.pressure')}</span>
                        <span className="font-medium">{device.data.pressure} bar</span>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{t('popup.lastUpdate')}</span>
                      <span className="font-medium">{device.data?.lastUpdate}</span>
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Map Legend */}
        <div className="absolute top-4 right-4 bg-[var(--surface)] rounded-lg shadow-lg p-4 min-w-[200px] z-10">
          <h3 className="font-semibold text-[var(--text-primary)] mb-3">{t('legend.title')}</h3>

          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)] mb-2">{t('legend.zoneStatus')}</p>
              <div className="space-y-1 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: '#4CAF50' }}></div>
                  <span>{t('legend.optimal')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: '#FF9800' }}></div>
                  <span>{t('legend.lowMoisture')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: '#2196F3' }}></div>
                  <span>{t('legend.highMoisture')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: '#F44336' }}></div>
                  <span>{t('legend.alert')}</span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-[var(--text-primary)] mb-2">{t('legend.devices')}</p>
              <div className="space-y-1 text-xs">
                <div className="flex items-center space-x-2">
                  <span>📡</span>
                  <span>{t('legend.gateway')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span>💧</span>
                  <span>{t('legend.valveNode')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span>⚡</span>
                  <span>{t('legend.motorNode')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span>📊</span>
                  <span>{t('legend.sensorNode')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Map;
